import React, { useState, useEffect, useRef } from "react";
import { Card, Input, Select, Tabs, Radio } from "antd";
import StandardButton from '../components/StandardButton';
import Footer from "../components/Footer";
import Header from "../components/Header";
import { useSearchParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { useTranslation } from "react-i18next";
import api from "../interceptors/api";

const { TextArea } = Input;
const { Option } = Select;
const { TabPane } = Tabs;

const ApiTester = () => {
  /* --------------------------- request state --------------------------- */
  const [method, setMethod] = useState("GET");
  const [url, setUrl] = useState("https://jsonplaceholder.typicode.com/posts");
  const [headers, setHeaders] = useState([["Content-Type", "application/json"]]);
  const [body, setBody] = useState("{}");
  const [bodyType, setBodyType] = useState("json");

  /* --------------------------- response state -------------------------- */
  const [response, setResponse] = useState(null);
  const [status, setStatus] = useState(null);
  const [renderType, setRenderType] = useState("raw");

  /* --------------------------- misc state ------------------------------ */
  const [history, setHistory] = useState([]);
  const [isJsonValid, setIsJsonValid] = useState(true);
  const [searchParams] = useSearchParams();
  const historyRef = useRef(null);
  const { t } = useTranslation();

  /* --------------------------- helpers -------------------------------- */
  const renderResponseBody = () => {
    if (!response) return null;
    const rawBody = response.body;

    if (renderType === "html") {
      return (
        <iframe
          title="HTML Response"
          srcDoc={rawBody}
          className="w-full h-96 border rounded-md bg-white"
        />
      );
    }
    if (renderType === "json") {
      return <pre className="mt-2 break-words">{JSON.stringify(rawBody, null, 2)}</pre>;
    }
    return (
      <pre className="mt-2 break-words">
        {typeof rawBody === "object" ? JSON.stringify(rawBody, null, 2) : rawBody}
      </pre>
    );
  };

  const renderResponseHeaders = () => {
    if (!response?.headers) return <em>{t("no_headers") || "No headers returned"}</em>;
    return (
      <pre className="whitespace-pre-wrap break-words">
        {Object.entries(response.headers)
          .map(([k, v]) => `${k}: ${v}`)
          .join("\n")}
      </pre>
    );
  };

  /* --------------------------- persistence ---------------------------- */
  useEffect(() => {
    if (history.length) localStorage.setItem("apiTesterHistory", JSON.stringify(history));
  }, [history]);

  useEffect(() => {
    const stored = localStorage.getItem("apiTesterHistory");
    if (stored) setHistory(JSON.parse(stored));
  }, []);

  /* --------------------------- shared link ---------------------------- */
  useEffect(() => {
    const id = searchParams.get("share");
    if (!id) return;

    api
      .get(`/api-tester/share/${id}`)
      .then(({ data }) => {
        if (data.url && data.method) {
          setUrl(data.url);
          setMethod(data.method);
          setHeaders(Object.entries(data.headers));
          setBody(data.body);
          setBodyType(data.bodyType);
        }
      })
      .catch(() => toast.error(t("server_error")));
  }, []);

  /* --------------------------- request logic -------------------------- */
  const sendRequest = async () => {
    const toastId = toast.loading(t("sending_request"));

    try {
      const parsedHeaders = headers.reduce(
        (acc, [k, v]) => (k.trim() ? { ...acc, [k]: v } : acc),
        {}
      );

      const { data } = await api.post("/api-tester", {
        url,
        method,
        headers: parsedHeaders,
        body: bodyType === "json" ? body : JSON.stringify(body),
      });

      toast.dismiss(toastId);

      if (data.error) {
        setStatus("Error");
        setResponse(data.error);
        toast.error(t("api_request_failed"));
        return;
      }

      setStatus(data.status);
      setResponse(data);
      toast.success(t("api_request_successful"));

      /* ---------- store in history (keep last 20) ------------ */
      setHistory((prev) => {
        const next = [{ url, method, headers, body, bodyType, timestamp: Date.now() }, ...prev];
        return next.slice(0, 20);
      });
    } catch (err) {
      toast.dismiss(toastId);
      setStatus("Error");
      setResponse(err.message);
      toast.error(t("something_went_wrong"));
    }
  };

  /* --------------------------- utils --------------------------------- */
  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(JSON.stringify(response, null, 2));
      toast.success(t("copy_success"));
    } catch {
      toast.error(t("failed_to_copy"));
    }
  };

  const handleShare = async () => {
    try {
      const { data } = await api.post("/api-tester/share", {
        url,
        method,
        headers: headers.reduce(
          (acc, [k, v]) => (k.trim() ? { ...acc, [k]: v } : acc),
          {}
        ),
        body,
        bodyType,
      });

      if (data.id) {
        const shareUrl = `${window.location.origin}/tools/api-tester?share=${data.id}`;
        await navigator.clipboard.writeText(shareUrl);
        toast.success(t("share_success"));
      } else {
        toast.error(t("share_failed"));
      }
    } catch {
      toast.error(t("share_failed"));
    }
  };

  const handleClearHistory = () => {
    setHistory([]);
    localStorage.removeItem("apiTesterHistory");
    toast.success(t("history_cleared"));
  };

  /* =================================================================== */

  return (
    <div className="min-h-screen bg-gradient-to-b from-white via-indigo-50 to-white p-0 text-gray-800">
      <Header />
      <div className="max-w-5xl mx-auto">
        {/* ----------------------------- editor card ---------------------------- */}
        <Card className="shadow-lg rounded-xl p-6">
          {/* Method & URL */}
          <div className="flex gap-2 mb-4">
            <Select value={method} onChange={setMethod} className="w-32">
              {["GET", "POST", "PUT", "PATCH", "DELETE", "HEAD", "OPTIONS"].map((m) => (
                <Option key={m}>{m}</Option>
              ))}
            </Select>
            <Input
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              placeholder={t("enter_api_url_placeholder")}
            />
          </div>

          {/* Request tabs */}
          <Tabs defaultActiveKey="headers">
            {/* ---- Headers tab ---- */}
            <TabPane tab={t("tab_headers")} key="headers">
              {headers.map(([k, v], idx) => (
                <div key={idx} className="flex gap-2 mb-2">
                  <Input
                    placeholder={t("key_placeholder")}
                    value={k}
                    onChange={(e) => {
                      const h = [...headers];
                      h[idx] = [e.target.value, v];
                      setHeaders(h);
                    }}
                  />
                  <Input
                    placeholder={t("value_placeholder")}
                    value={v}
                    onChange={(e) => {
                      const h = [...headers];
                      h[idx] = [k, e.target.value];
                      setHeaders(h);
                    }}
                  />
                  <StandardButton type="secondary" danger onClick={() => setHeaders(headers.filter((_, i) => i !== idx))}>
                    {t("remove_button")}
                  </StandardButton>
                </div>
              ))}
              <StandardButton type="secondary" onClick={() => setHeaders([...headers, ["", ""]])}>
                {t("add_header_button")}
              </StandardButton>
            </TabPane>

            {/* ---- Body tab ---- */}
            <TabPane tab={t("tab_body")} key="body">
              {/* Body type selector with StandardButton components */}
              <div className="flex items-center gap-2 mb-4">
                <StandardButton
                  type={bodyType === "json" ? "primary" : "secondary"}
                  size="small"
                  onClick={() => setBodyType("json")}
                  className={bodyType === "json" ? "rounded-full" : "rounded-full border-gray-300"}
                >
                  JSON
                </StandardButton>
                <StandardButton
                  type={bodyType === "text" ? "primary" : "secondary"}
                  size="small"
                  onClick={() => setBodyType("text")}
                  className={bodyType === "text" ? "rounded-full" : "rounded-full border-gray-300"}
                >
                  Text
                </StandardButton>
              </div>
              <TextArea
                rows={6}
                value={body}
                placeholder={
                  bodyType === "json"
                    ? t("json_body_placeholder")
                    : t("text_body_placeholder")
                }
                onChange={(e) => {
                  setBody(e.target.value);
                  if (bodyType === "json") {
                    try {
                      JSON.parse(e.target.value);
                      setIsJsonValid(true);
                    } catch {
                      setIsJsonValid(false);
                    }
                  }
                }}
                style={{
                  color: bodyType === "json" && !isJsonValid ? "#dc2626" : undefined,
                }}
              />
            </TabPane>
          </Tabs>

          {/* Send button */}
          <StandardButton
            type="secondary"
            className="w-full mt-4"
            onClick={sendRequest}
          >
            {t("send_button")}
          </StandardButton>

          {/* --------------------- response panel ---------------------- */}
          {status && (
            <div className="bg-gray-100 p-4 mt-6 rounded border border-gray-300">
              <strong>{t("status_label")}</strong> {status}
            </div>
          )}

          {response && (
            <div className="bg-indigo-50 p-4 mt-4 rounded border border-indigo-200 text-sm overflow-auto max-h-[40rem]">
              {/* copy & share */}
              <div className="flex gap-3 mb-2">
                <StandardButton
                  type="secondary"
                  size="small"
                  onClick={handleCopy}
                >
                  {t("copy")}
                </StandardButton>
                <StandardButton
                  type="secondary"
                  size="small"
                  onClick={handleShare}
                >
                  {t("share")}
                </StandardButton>
              </div>

              {/* tabs: body | headers */}
              <Tabs defaultActiveKey="body">
                <TabPane tab={t("response_label") || "Body"} key="body">
                  {/* Response type selector with StandardButton components */}
                  <div className="flex justify-end mb-2 gap-2">
                    <StandardButton
                      type={renderType === "raw" ? "primary" : "secondary"}
                      size="small"
                      onClick={() => setRenderType("raw")}
                      className={renderType === "raw" ? "rounded-full" : "rounded-full border-gray-300"}
                    >
                      Raw
                    </StandardButton>
                    <StandardButton
                      type={renderType === "json" ? "primary" : "secondary"}
                      size="small"
                      onClick={() => setRenderType("json")}
                      className={renderType === "json" ? "rounded-full" : "rounded-full border-gray-300"}
                    >
                      JSON
                    </StandardButton>
                    <StandardButton
                      type={renderType === "html" ? "primary" : "secondary"}
                      size="small"
                      onClick={() => setRenderType("html")}
                      className={renderType === "html" ? "rounded-full" : "rounded-full border-gray-300"}
                    >
                      HTML
                    </StandardButton>
                  </div>
                  {renderResponseBody()}
                </TabPane>

                <TabPane tab={t("tab_headers")} key="headers">
                  <div className="bg-white p-3 border rounded">
                    {renderResponseHeaders()}
                  </div>
                </TabPane>
              </Tabs>
            </div>
          )}
        </Card>

        {/* --------------------- history panel ---------------------- */}
        {history.length > 0 && (
          <Card className="shadow-lg rounded-xl p-4 mt-6" ref={historyRef}>
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-bold">{t("history") || "History"}</h2>
              <StandardButton type="secondary" size="small" danger onClick={handleClearHistory}>
                {t("clear_history_button") || "Clear History"}
              </StandardButton>
            </div>

            <div className="flex flex-col gap-4">
              {history.map((item, idx) => (
                <div
                  key={idx}
                  className="p-3 border rounded hover:bg-gray-100 cursor-pointer"
                  onClick={() => {
                    setUrl(item.url);
                    setMethod(item.method);
                    setHeaders(item.headers);
                    setBody(item.body);
                    setBodyType(item.bodyType);
                    window.scrollTo({ top: 0, behavior: "smooth" });
                  }}
                >
                  <div>
                    <strong>{t("method_label") || "Method"}:</strong> {item.method}
                  </div>
                  <div>
                    <strong>{t("url_label") || "URL"}:</strong> {item.url}
                  </div>
                  <div className="text-sm text-gray-500">
                    {new Date(item.timestamp).toLocaleString()}
                  </div>
                </div>
              ))}
            </div>
          </Card>
        )}
      </div>
      <Footer />
    </div>
  );
};

export default ApiTester;
